import React, { useState, useEffect, useRef, useCallback } from 'react';
import socketService from '../../services/socket';
import notificationService from '../../services/notificationService';

const SOUND_URL = "https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3";

const NotificationBell = () => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [soundEnabled, setSoundEnabled] = useState(() => {
        const saved = localStorage.getItem('idacab-notification-sound');
        return saved !== null ? saved === 'true' : true;
    });
    
    const dropdownRef = useRef(null);
    // audioRef starts null — created lazily after a user gesture to satisfy browser autoplay policy
    const audioRef = useRef(null);
    const soundEnabledRef = useRef(soundEnabled);

    // Get current user from localStorage
    const currentUser = useRef(null);
    useEffect(() => {
        try {
            const u = JSON.parse(localStorage.getItem('user'));
            currentUser.current = u;
        } catch(e) {}
    }, []);

    // Lazily create the Audio object on first user gesture so the browser
    // autoplay policy doesn't block subsequent notification sounds.
    const ensureAudio = useCallback(() => {
        if (!audioRef.current) {
            audioRef.current = new Audio(SOUND_URL);
            audioRef.current.preload = 'auto';
        }
    }, []);

    useEffect(() => {
        // Prime audio context on the very first click anywhere on the page.
        const prime = () => { ensureAudio(); document.removeEventListener('click', prime); };
        document.addEventListener('click', prime);
        return () => document.removeEventListener('click', prime);
    }, [ensureAudio]);


    const getNotificationStyle = (type) => {
        switch (type) {
            case 'booking_new':
            case 'job_new':
                return { bg: 'bg-emerald-100', text: 'text-emerald-600', icon: (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                )};
            case 'booking_update':
            case 'job_update':
            case 'vehicle_update':
            case 'route_update':
                return { bg: 'bg-blue-100', text: 'text-blue-600', icon: (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                )};
            case 'booking_status':
            case 'document_update':
                return { bg: 'bg-amber-100', text: 'text-amber-600', icon: (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                )};
            case 'mission_issue':
                return { bg: 'bg-rose-100', text: 'text-rose-600', icon: (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                )};
            default:
                return { bg: 'bg-slate-100', text: 'text-slate-600', icon: (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                )};
        }
    };

    const fetchNotifications = useCallback(async () => {
        try {
            const res = await notificationService.getNotifications();
            if (res.data.success) {
                setNotifications(res.data.data);
                setUnreadCount(res.data.unreadCount || 0);
            }
        } catch (err) {
            console.error('Fetch notifications error:', err);
        }
    }, []);

    useEffect(() => {
        fetchNotifications();

        // Polling fallback: re-fetch every 15 seconds for guaranteed sync
        const pollInterval = setInterval(fetchNotifications, 15000);

        // Listen for real-time notifications via socket
        const handleNewNotification = (newNotif) => {
            const user = currentUser.current;
            if (!user) return;

            // FILTER: Only accept notifications meant for THIS user
            // Use toString() on both sides to handle ObjectId vs string type differences
            if (newNotif.recipientId && newNotif.recipientId.toString() !== user._id.toString()) {
                return;
            }
            if (newNotif.recipientRoles && !newNotif.recipientRoles.includes(user.role)) {
                return;
            }

            // Add to local state with isRead = false
            const enriched = { ...newNotif, isRead: false };
            setNotifications(prev => [enriched, ...prev.slice(0, 49)]);
            setUnreadCount(prev => prev + 1);
            
            // Play sound if enabled (use ref to avoid stale closure)
            if (soundEnabledRef.current) {
                ensureAudio();
                if (audioRef.current) {
                    audioRef.current.currentTime = 0;
                    audioRef.current.play().catch(() => {});
                }
            }
        };
        socketService.on('notification:new', handleNewNotification);

        // Close dropdown on click outside
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            clearInterval(pollInterval);
            socketService.off('notification:new', handleNewNotification);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchNotifications]);

    const toggleDropdown = () => {
        ensureAudio();
        setIsOpen(!isOpen);
    };

    const toggleSound = (e) => {
        e.stopPropagation();
        ensureAudio();
        const newState = !soundEnabled;
        setSoundEnabled(newState);
        soundEnabledRef.current = newState; // Keep ref in sync
        localStorage.setItem('idacab-notification-sound', String(newState));
        if (newState && audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => {});
        }
    };

    const handleNotificationClick = async (notif) => {
        // Expand in-place within the container
        setExpandedId(notif._id === expandedId ? null : notif._id);
        
        try {
            if (notif._id && !notif.isRead) {
                await notificationService.markAsRead(notif._id);
                setNotifications(prev => prev.map(n => 
                    n._id === notif._id ? { ...n, isRead: true } : n
                ));
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (err) {
            console.error('Mark read error:', err);
        }
    };

    const handleMarkAllRead = async (e) => {
        e.stopPropagation();
        try {
            await notificationService.markAllAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            setUnreadCount(0);
        } catch (err) {
            console.error('Mark all read error:', err);
        }
    };

    // Show all notifications in the dropdown
    const displayedNotifications = notifications;

    return (
        <>
            <div className="relative z-20" ref={dropdownRef}>
                <button
                    onClick={toggleDropdown}
                    type="button"
                    className="p-2.5 rounded-xl bg-slate-50 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all relative border border-slate-200/60 active:scale-95 z-30 pointer-events-auto dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-slate-700 dark:hover:text-emerald-400"
                >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    {unreadCount > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 flex h-5 min-w-[20px]">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-5 min-w-[20px] px-1 bg-rose-500 text-[10px] font-bold text-white items-center justify-center">
                                {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                        </span>
                    )}
                </button>

                {isOpen && (
                    <div className="fixed left-4 right-4 top-[85px] mx-auto sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mx-0 sm:mt-2 lg:mt-3 bg-white/95 dark:bg-slate-900/90 backdrop-blur-3xl rounded-[2rem] shadow-2xl shadow-slate-200/50 dark:shadow-black/50 border border-white/60 dark:border-white/10 overflow-hidden z-[100] animate-in fade-in slide-in-from-top-2 duration-300 w-[calc(100vw-32px)] sm:w-96 max-w-sm sm:max-w-none">
                        {/* Header */}
                        <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-900/50">
                            <div className="flex items-center gap-2">
                                <h3 className="text-sm font-bold text-slate-900 dark:text-white">Notifications</h3>
                                <button 
                                    onClick={toggleSound}
                                    className={`p-1.5 rounded-lg transition-colors ${soundEnabled ? 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100 dark:text-emerald-400 dark:bg-emerald-500/20 dark:hover:bg-emerald-500/30' : 'text-slate-400 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700'}`}
                                    title={soundEnabled ? "Mute notifications" : "Unmute notifications"}
                                >
                                    {soundEnabled ? (
                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            <div className="flex items-center gap-2">
                                {unreadCount > 0 && (
                                    <button 
                                        onClick={handleMarkAllRead}
                                        className="text-[10px] font-semibold text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1 rounded-lg transition-colors"
                                    >
                                        <svg className="w-3.5 h-3.5 flex-shrink-0 mr-1 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg> Mark All Read
                                    </button>
                                )}
                                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                                    {unreadCount > 0 ? `${unreadCount} New` : `${notifications.length} Total`}
                                </span>
                            </div>
                        </div>
                        
                        {/* Notification List */}
                        <div className="max-h-[380px] overflow-y-auto">
                            {displayedNotifications.length === 0 ? (
                                <div className="p-8 text-center">
                                    <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-3">
                                        <svg className="w-6 h-6 text-slate-300 dark:text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                        </svg>
                                    </div>
                                    <p className="text-xs text-slate-400 font-medium">All caught up!</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-slate-50 dark:divide-slate-800">
                                    {displayedNotifications.map((notif, idx) => {
                                        const style = getNotificationStyle(notif.type);
                                        const isExpanded = expandedId === notif._id;
                                        return (
                                        <button
                                            key={notif._id || idx}
                                            onClick={() => handleNotificationClick(notif)}
                                            className={`w-full p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors text-left flex gap-3 group ${!notif.isRead ? 'bg-emerald-50/30 dark:bg-emerald-500/10' : ''}`}
                                        >
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${style.bg} ${style.text}`}>
                                                {style.icon}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-xs font-bold group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors ${!notif.isRead ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400'}`}>{notif.title}</p>
                                                <p className={`text-[11px] text-slate-500 mt-0.5 leading-relaxed ${isExpanded ? 'whitespace-pre-wrap' : 'line-clamp-2'}`}>{notif.message}</p>
                                                <p className="text-[10px] text-slate-400 mt-1.5 font-medium">
                                                    {notif.createdAt ? new Date(notif.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Just now'}
                                                </p>
                                            </div>
                                            {!notif.isRead && (
                                                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0 animate-pulse" />
                                            )}
                                        </button>
                                    )})}
                                </div>
                            )}
                        </div>
                        
                    </div>
                )}
            </div>
        </>
    );
};

export default NotificationBell;
